import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, MapPin, Camera, Video, Receipt, FileText, Users, BadgeCheck, Sprout, UserCheck, TrendingUp, Map, BookOpen, Workflow, Store, Newspaper, MessageCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { JourneySteps, Lifecycle, OfftakePrinciples, JOURNEY_TITLE, JOURNEY_SUB } from "./InfoPages";
import { ContactSJI } from "../../components/public/ContactSJI";

const Section = ({ id, eyebrow, title, children, className = "" }) => (
  <section id={id} className={`py-16 sm:py-20 lg:py-24 ${className}`} data-testid={`section-${id}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-3xl">{title}</h2>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="home-hero">
    <img src="/images/coffee-hero.jpg" alt="Kebun kopi di lereng perbukitan" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/75 to-emerald-950/25" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:py-20 lg:py-24">
      <div className="max-w-2xl fade-up">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/25 px-3 py-1 text-xs font-semibold text-emerald-50">
          <ShieldCheck className="h-3.5 w-3.5" /> Platform Pengembangan & Monitoring Kebun Kopi
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" data-testid="hero-title">
          SALIMBADO FARM<br /><span className="text-amber-300 text-3xl sm:text-4xl lg:text-5xl">Mengubah Lahan Kosong atau Belum Dikelola Menjadi Kebun Kopi Produktif</span>
        </h1>
        <p className="mt-5 text-sm sm:text-base text-emerald-50/90 leading-relaxed max-w-xl" data-testid="hero-subtitle">
          Platform pengembangan dan monitoring kebun kopi yang membantu Pemilik Lahan, Pengelola, Pemodal Kebun, dan PT SJI bekerja dalam satu sistem yang transparan.
        </p>
        <a href="#mulai" className="mt-5 sm:mt-7 inline-flex items-center gap-2 text-sm font-bold text-amber-300 hover:text-amber-200" data-testid="hero-start-link">Pilih peran Anda di bawah <ArrowRight className="h-4 w-4 rotate-90" /></a>
        <p className="mt-6 hidden sm:block text-xs sm:text-sm text-emerald-100/80" data-testid="hero-trust">Lahan tercatat & terpetakan • Kebun terpantau • Aktivitas terdokumentasi • Dukungan pasar PT SJI</p>
      </div>
    </div>
  </section>
);

const PATHS = [
  { id: "land", icon: Sprout, title: "Ajukan Lahan Anda Menjadi Kebun Kopi", text: "Punya lahan yang berpotensi dikembangkan menjadi kebun kopi? Ajukan data lahan Anda untuk ditinjau melalui Salimbado Farm.", cta: "AJUKAN LAHAN ANDA MENJADI KEBUN KOPI", to: "/ajukan-lahan", cls: "bg-amber-400 text-emerald-950 hover:bg-amber-300" },
  { id: "manager", icon: UserCheck, title: "Daftar Menjadi Pengelola Kebun Kopi", text: "Memiliki pengalaman dan kemampuan mengelola kebun kopi? Daftarkan diri sebagai Pengelola Perorangan.", cta: "DAFTAR MENJADI PENGELOLA KEBUN KOPI", to: "/daftar-pengelola", cls: "bg-emerald-800 text-white hover:bg-emerald-900" },
  { id: "investor", icon: TrendingUp, title: "Ingin Menjadi Pemodal Kebun?", text: "Pilih Rencana Kebun yang tersedia dan pelajari lahan, Pengelola, kebutuhan modal, serta rencana pengembangannya.", cta: "INGIN MENJADI PEMODAL KEBUN?", to: "/ingin-menjadi-pemodal", cls: "bg-slate-900 text-white hover:bg-slate-800", sub: { to: "/cara-kerja", label: "Pelajari cara pendanaan kebun" } },
];

const StartHere = () => (
  <section id="mulai" className="relative -mt-8 sm:-mt-10 pb-12 sm:pb-16 scroll-mt-20" data-testid="home-start-here">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="rounded-3xl bg-white shadow-xl ring-1 ring-slate-200 p-5 sm:p-8">
        <p className="eyebrow">Mulai dari sini</p>
        <h2 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900">Pilih jalur yang sesuai dengan Anda</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {PATHS.map((p) => (
            <div key={p.id} className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50/60 p-5 hover:border-emerald-400 hover:-translate-y-0.5 transition-[transform,border-color]" data-testid={`start-card-${p.id}`}>
              <p.icon className="h-9 w-9 text-emerald-800" />
              <h3 className="mt-3 text-lg font-extrabold text-slate-900 leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 flex-1">{p.text}</p>
              <Link to={p.to} className={`mt-5 inline-flex min-h-12 items-center justify-center rounded-full px-5 text-center text-sm font-extrabold ${p.cls}`} data-testid={`start-cta-${p.id}`}>{p.cta}</Link>
              {p.sub && <Link to={p.sub.to} className="mt-2 text-center text-xs font-semibold text-emerald-800 hover:underline" data-testid={`start-sub-${p.id}`}>{p.sub.label}</Link>}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const EXPLORE = [[Map, "Lihat Rencana Kebun Tersedia", "/peluang-investasi", "opportunities"], [BookOpen, "Cara Pendanaan Kebun", "/cara-kerja", "how"],
  [Workflow, "Cara Kerja", "/cara-kerja#alur", "workflow"], [Users, "Pengelola Terdaftar", "/kelompok-tani", "managers"], [Sprout, "Kebun Aktif", "/monitoring-kebun", "active-farms"],
  [Store, "PT SJI Offtake / Pasar", "/offtake-pt-sji", "offtake"], [Newspaper, "Berita / Update", "/berita", "news"], [MessageCircle, "Hubungi PT SJI", "#kontak", "contact"]];

const Explore = () => (
  <section className="py-12 sm:py-16 bg-slate-50" data-testid="home-explore">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="eyebrow">Jelajahi Salimbado Farm</p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {EXPLORE.map(([I, label, to, id]) => (to.startsWith("#")
          ? <a key={id} href={to} className="flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-3 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-400" data-testid={`explore-${id}`}><I className="h-4 w-4 text-emerald-800 shrink-0" />{label}</a>
          : <Link key={id} to={to} className="flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-3 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-400" data-testid={`explore-${id}`}><I className="h-4 w-4 text-emerald-800 shrink-0" />{label}</Link>))}
      </div>
    </div>
  </section>
);

const VacantToProductive = () => (
  <Section id="story" eyebrow="Dari Lahan Kosong" title="Lahan kosong menjadi kebun kopi produktif">
    <div className="grid gap-10 lg:grid-cols-2 items-center">
      <div className="space-y-5 text-slate-600 leading-relaxed">
        <p>Salimbado Farm tidak sekadar menawarkan kebun yang sudah ada. Platform ini menghadirkan <b className="text-slate-900">lahan kosong yang diajukan Kelompok Tani/Koperasi maupun Pemilik Lahan Perorangan</b> yang dapat dibangun menjadi perkebunan kopi.</p>
        <p>Pemodal Kebun membiayai pembangunan kebun tertentu sejak awal — dari pembersihan lahan dan penanaman bibit hingga panen — sesuai Perjanjian Kebun, dan memantau setiap perkembangannya secara digital.</p>
        <Lifecycle />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <figure><img src="/images/lahan-kosong-awal.webp" alt="Lahan kosong berumput di perbukitan Salimbado Farm saat matahari terbenam" data-testid="home-start-photo" loading="lazy" className="aspect-[4/5] w-full object-cover object-center rounded-2xl" /><figcaption className="mt-2 text-xs font-semibold text-slate-600">AWAL: Lahan kosong</figcaption></figure>
        <figure className="mt-10"><img src="/images/kebun-kopi-tujuan.webp" alt="Kebun kopi produktif Salimbado Farm saat matahari terbenam dan buah kopi berbuah lebat" data-testid="home-goal-photo" loading="lazy" className="aspect-[4/5] w-full object-cover object-center rounded-2xl" /><figcaption className="mt-2 text-xs font-semibold text-slate-600">TUJUAN: Kebun kopi produktif</figcaption></figure>
        <p className="col-span-2 text-[11px] text-slate-500">Foto ilustrasi desain, bukan bukti kebun.</p>
      </div>
    </div>
  </Section>
);

const HowItWorks = () => (
  <Section id="how" eyebrow="Cara Kerja" title={JOURNEY_TITLE} className="bg-white border-y border-slate-200/70">
    <p className="-mt-4 mb-8 max-w-3xl text-base md:text-lg text-slate-600" data-testid="home-journey-subtitle">{JOURNEY_SUB}</p>
    <JourneySteps />
  </Section>
);

const EVIDENCE = [[Clock, "Tanggal"], [MapPin, "GPS"], [Camera, "Foto"], [Video, "Video"], [Receipt, "Biaya"], [FileText, "Invoice & kuitansi"], [Users, "Identitas kelompok tani"], [BadgeCheck, "Verifikasi PT SJI"]];

const Transparency = () => (
  <Section id="transparency" eyebrow="Transparansi Lapangan" title="Setiap kegiatan penting di kebun kopi didukung bukti">
    <div className="grid gap-10 lg:grid-cols-2 items-center">
      <ul className="grid grid-cols-2 gap-3">
        {EVIDENCE.map(([I, t]) => <li key={t} className="flex items-center gap-3 bg-white border border-emerald-900/10 rounded-xl p-4 text-sm font-semibold text-slate-800"><I className="h-5 w-5 text-emerald-800 shrink-0" />{t}</li>)}
      </ul>
      <img src="/images/coffee-picking.jpg" alt="Petani memetik cherry kopi" className="w-full aspect-[16/11] object-cover rounded-2xl" />
    </div>
  </Section>
);

const OfftakeSupport = () => (
  <Section id="offtake" eyebrow="PT SJI Offtake Support" title="Dukungan pasar untuk hasil kopi yang memenuhi mutu" className="bg-emerald-950 [&_h2]:text-white [&_.eyebrow]:text-amber-300">
    <div className="grid gap-10 lg:grid-cols-2 items-center">
      <div className="space-y-5 text-emerald-50/85 leading-relaxed [&_li]:text-emerald-50/90">
        <p>Penjualan kepada pembeli selain PT SJI diperbolehkan apabila menawarkan harga bersih yang lebih baik. Harga ditentukan berdasarkan mutu kopi, grade, kondisi pasar, dan ketentuan transaksi pada saat penjualan. PT SJI dapat menjadi pembeli pendukung/offtaker apabila diperlukan. PT SJI tidak bertanggung jawab atas pembayaran atau kewajiban pembeli eksternal.</p>
        <OfftakePrinciples />
        <Button asChild variant="outline" className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white" data-testid="home-offtake-link"><Link to="/offtake-pt-sji">Selengkapnya</Link></Button>
      </div>
      <img src="/images/coffee-greenbean.jpg" alt="Green bean kopi" className="w-full aspect-[16/11] object-cover rounded-2xl" />
    </div>
  </Section>
);


export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <StartHere />
      <VacantToProductive />
      <HowItWorks />
      <Transparency />
      <OfftakeSupport />
      <Explore />
      <div id="kontak" className="scroll-mt-20"><ContactSJI testid="home-contact" /></div>
    </div>
  );
}
